import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Linkedin, MessageCircle } from "lucide-react";
import { CONTACT, NAV } from "../data/site";
import CookieConsent from "./CookieConsent";
import { EASE } from "./Reveal";

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-hairline bg-sand/85 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 lg:px-12">
        <Link to="/" className="font-serif text-xl tracking-[0.18em] text-graphite" data-testid="logo-link">
          PURTIER<span className="text-burgundy">.BG</span>
        </Link>
        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={item.testid}
              className={({ isActive }) =>
                `group relative text-[13px] uppercase tracking-[0.14em] transition-colors duration-500 ${
                  isActive ? "text-burgundy" : "text-graphite/70 hover:text-graphite"
                }`
              }
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-burgundy transition-[width] duration-500 group-hover:w-full" />
            </NavLink>
          ))}
          <Link
            to="/kontakti"
            data-testid="header-cta-button"
            className="border border-burgundy bg-transparent px-6 py-2.5 text-[12px] uppercase tracking-[0.2em] text-burgundy transition-colors duration-[400ms] hover:bg-burgundy hover:text-sand"
          >
            Запазете безплатна консултация
          </Link>
        </nav>
        <button
          className="lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Меню"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="overflow-hidden border-t border-hairline bg-sand lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col px-6 py-6">
              {NAV.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-${item.testid}`}
                  className={({ isActive }) =>
                    `border-b border-hairline py-4 font-serif text-2xl ${isActive ? "text-burgundy" : "text-graphite"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/kontakti"
                data-testid="mobile-cta-button"
                className="mt-6 border border-burgundy bg-transparent px-6 py-4 text-center text-[12px] uppercase tracking-[0.2em] text-burgundy transition-colors duration-[400ms]"
              >
                Запазете безплатна консултация
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-hairline bg-graphite text-sand" data-testid="site-footer">
    <div className="mx-auto grid max-w-[1400px] gap-12 px-6 py-20 md:grid-cols-3 lg:px-12">
      <div>
        <p className="font-serif text-2xl tracking-[0.15em]">
          PURTIER<span className="text-sand/50">.BG</span>
        </p>
        <p className="mt-6 max-w-xs text-sm leading-relaxed text-sand/60">
          Личен консултант за PURTIER седмо поколение. Индивидуален подход за клиенти от цяла България.
        </p>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.3em] text-sand/40">Навигация</p>
        <div className="mt-6 flex flex-col gap-3">
          {NAV.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`footer-${item.testid}`}
              className="text-sm text-sand/70 transition-colors duration-500 hover:text-sand"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.3em] text-sand/40">Контакти</p>
        <div className="mt-6 flex flex-col gap-3 text-sm text-sand/70">
          <a href={CONTACT.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-3 transition-colors duration-500 hover:text-sand">
            <Phone size={14} strokeWidth={1.5} /> {CONTACT.phoneDisplay}
          </a>
          <a href={CONTACT.emailHref} data-testid="footer-email-link" className="transition-colors duration-500 hover:text-sand">
            {CONTACT.email}
          </a>
          <a
            href="https://www.linkedin.com/in/bilyana-d-52852142"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-linkedin-link"
            className="flex items-center gap-3 transition-colors duration-500 hover:text-sand"
          >
            <Linkedin size={14} strokeWidth={1.5} /> LinkedIn
          </a>
          <p className="text-sand/50">Работя и с клиенти извън България – онлайн и по телефона.</p>
        </div>
      </div>
    </div>
    <div className="border-t border-sand/10">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-6 py-8 text-[11px] leading-relaxed text-sand/40 lg:px-12">
        <p>
          PURTIER е хранителна добавка, а не лекарствен продукт. Не е предназначен за диагностициране, лечение или
          предпазване от заболявания и не замества разнообразното и балансирано хранене.
        </p>
        <p>© {new Date().getFullYear()} PURTIER.BG – Всички права запазени.</p>
        <p>
          <Link to="/poveritelnost" data-testid="footer-privacy-link" className="underline-offset-4 transition-colors duration-500 hover:text-sand hover:underline">
            Политика за поверителност
          </Link>
        </p>
      </div>
    </div>
  </footer>
);

const FloatingCTA = () => (
  <nav
    aria-label="Бърза връзка с Биляна Димова"
    data-testid="floating-cta"
    className="fixed right-4 z-40 flex flex-col gap-2 sm:right-6"
    style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}
  >
    {[
      { label: "Viber", href: CONTACT.viber, testid: "floating-viber-button" },
      { label: "WhatsApp", href: CONTACT.whatsapp, testid: "floating-whatsapp-button", external: true },
    ].map((channel) => (
      <a
        key={channel.label}
        href={channel.href}
        target={channel.external ? "_blank" : undefined}
        rel={channel.external ? "noopener noreferrer" : undefined}
        data-testid={channel.testid}
        aria-label={`Пишете ми във ${channel.label}`}
        className="flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-[#A11D35] px-4 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-[#80172A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#A11D35]"
      >
        <MessageCircle size={18} strokeWidth={1.5} aria-hidden="true" />
        {channel.label}
      </a>
    ))}
  </nav>
);

export const Layout = ({ children }) => (
  <div className="min-h-screen bg-sand" data-testid="site-layout">
    <Header />
    <main className="pt-[73px]">{children}</main>
    <Footer />
    <FloatingCTA />
    <CookieConsent />
    <div className="grain-overlay" aria-hidden="true" />
  </div>
);
